// src/components/NotaireTransmissionCartes.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Vue NOTAIRE de la « carte de transmission » des héritiers d'un client.
//
// Architecture : le profil (archétype) est calculé UNE fois par le moteur
// canonique de l'app vision (repo ArtkivVisionApp) et persisté en write-through
// dans `heir_transmission_profiles`. Ici on LIT ce cache — aucune duplication de
// la logique ni des 32 questions. L'accès est gaté par la RLS : le notaire ne
// voit ces cartes QUE si le client l'a explicitement autorisé
// (heir_transmission_grants actif). Le titre professionnel ne suffit jamais.
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";

const AXIS_ORDER = ["temporal", "perception", "social", "openness", "motivation"];

const ARCH_COLOR = {
  batisseur: "#2563eb", passionne: "#dc2626", connecteur: "#7E5A8B",
  gardien: "#0f766e", mecene: "#c9a961",
};

// Contenu repris à l'identique de l'app vision (i18n vision.archetype.*).
const CONTENT = {
  fr: {
    title: "Carte de transmission des héritiers",
    subtitle: "Le rapport à l'art de chaque héritier, autorisé par le client. Aide à préparer une transmission juste — sans valeur financière.",
    yourArchetype: "Archétype",
    affinity: (p) => `${p}% d'affinité`,
    risk: "Point de vigilance",
    need: "Ce qu'Artkiv apporte",
    axes: "Profil",
    profileOf: (n) => `Profil de ${n}`,
    none: "Aucune carte de transmission disponible : soit le client ne vous a pas (encore) autorisé, soit les questionnaires héritiers ne sont pas encore complétés.",
    axis: { temporal: "Rapport au temps", perception: "Perception", social: "Posture sociale", openness: "Ouverture", motivation: "Motivation" },
    arch: {
      batisseur: { name: "Le Bâtisseur", tagline: "Lit l'art comme un portefeuille : cotes, carrières, tendances.", risk: "Une documentation centrée sur la valeur de revente, qui néglige provenance, état et contexte curatorial.", need: "Tracer provenance et conservation — ce qui justifiera la valeur au-delà de la cote." },
      passionne: { name: "Le Passionné", tagline: "Achète au coup de cœur ; la collection raconte une histoire intime.", risk: "Peu de traçabilité, des acquisitions dispersées, aucune vue d'ensemble pour les héritiers.", need: "Construire la vue d'ensemble et le mode d'emploi de la collection." },
      connecteur: { name: "Le Connecteur", tagline: "Collectionne au fil des rencontres : artistes, curateurs, galeristes.", risk: "Une documentation qui vit dans la mémoire et les relations, pas sur le papier.", need: "Coucher sur le papier les provenances et les liens qui font la collection." },
      gardien: { name: "Le Gardien", tagline: "Pense déjà legs, institution, fondation — la transmission l'habite.", risk: "Une intention forte, mais rarement formalisée en dossier juridique et fiscal réel.", need: "Transformer l'intention en dossier de transmission constitué." },
      mecene: { name: "Le Mécène", tagline: "Collectionne pour soutenir : artistes, institutions, causes.", risk: "Une collection dispersée (dons, prêts, financements) dont la valeur patrimoniale globale devient illisible.", need: "Consolider une valorisation lisible malgré la dispersion." },
    },
  },
  en: {
    title: "Heirs' transmission map",
    subtitle: "Each heir's relationship to art, authorized by the client. Helps prepare a fair transmission — no financial value.",
    yourArchetype: "Archetype",
    affinity: (p) => `${p}% affinity`,
    risk: "Watch-out",
    need: "What Artkiv brings",
    axes: "Profile",
    profileOf: (n) => `${n}'s profile`,
    none: "No transmission map available: either the client has not (yet) authorized you, or the heir questionnaires are not completed.",
    axis: { temporal: "Relationship to time", perception: "Perception", social: "Social stance", openness: "Openness", motivation: "Motivation" },
    arch: {
      batisseur: { name: "The Builder", tagline: "Reads art like a portfolio: ratings, careers, trends.", risk: "Documentation focused on resale value, neglecting provenance, condition and curatorial context.", need: "Track provenance and conservation — what will justify value beyond the rating." },
      passionne: { name: "The Aficionado", tagline: "Buys on impulse; the collection tells an intimate story.", risk: "Little traceability, scattered acquisitions, no overview for the heirs.", need: "Build the collection's overview and instruction manual." },
      connecteur: { name: "The Connector", tagline: "Collects through encounters: artists, curators, gallerists.", risk: "Documentation that lives in memory and relationships, not on paper.", need: "Put on paper the provenances and connections that make the collection." },
      gardien: { name: "The Keeper", tagline: "Already thinks legacy, institution, foundation — transmission drives them.", risk: "A strong intention, rarely formalised into a real legal and fiscal dossier.", need: "Turn intention into a fully constituted transmission dossier." },
      mecene: { name: "The Benefactor", tagline: "Collects to support: artists, institutions, causes.", risk: "A dispersed collection (gifts, loans, funding) whose overall patrimonial value becomes illegible.", need: "Consolidate a legible valuation despite dispersion." },
    },
  },
};

function pickLang() {
  const l = (typeof navigator !== "undefined" && navigator.language ? navigator.language : "fr").slice(0, 2).toLowerCase();
  return CONTENT[l] || CONTENT.fr;
}

function Carte({ profile, name, C }) {
  const key = profile.primary_archetype;
  const a = C.arch[key] || { name: key, tagline: "", risk: "", need: "" };
  const color = ARCH_COLOR[key] || "#8B7355";
  const ranking = Array.isArray(profile.ranking) ? profile.ranking : [];
  const affinity = Math.round(ranking[0]?.affinity ?? 0);
  const axes = profile.axes || {};

  return (
    <div style={{ background: "#fdfbf6", border: "1px solid #e5e0d8", borderRadius: 16, padding: "1.5rem", fontFamily: "Georgia, serif", color: "#1a1a1a" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.75rem" }}>
        <div style={{ fontSize: "0.7rem", letterSpacing: "0.08em", textTransform: "uppercase", color: "#8a7650" }}>{C.title}</div>
        <div style={{ fontSize: "0.8rem", color: "#6b6459" }}>{C.profileOf(name)}</div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.1rem" }}>
        <div style={{ width: 54, height: 54, borderRadius: "50%", flexShrink: 0, background: color + "1a", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ width: 16, height: 16, borderRadius: "50%", background: color }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: "0.68rem", color: "#8a8378" }}>{C.yourArchetype}</div>
          <div style={{ fontSize: "1.25rem", fontWeight: 700, color, lineHeight: 1.1 }}>{a.name}</div>
          <div style={{ fontSize: "0.8rem", color: "#4a453d", marginTop: 3 }}>{a.tagline}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "1.5rem", fontWeight: 700, color }}>{affinity}%</div>
          <div style={{ fontSize: "0.64rem", color: "#8a8378" }}>{C.affinity(affinity)}</div>
        </div>
      </div>

      {/* Axes */}
      <div style={{ marginBottom: "1.1rem" }}>
        <div style={{ fontSize: "0.7rem", color: "#8a8378", marginBottom: "0.5rem" }}>{C.axes}</div>
        {AXIS_ORDER.map((ax) => {
          const v = Math.round(Number(axes[ax] ?? 50));
          return (
            <div key={ax} style={{ display: "flex", alignItems: "center", gap: "0.7rem", marginBottom: "0.35rem" }}>
              <div style={{ width: 130, fontSize: "0.76rem", color: "#4a453d" }}>{C.axis[ax]}</div>
              <div style={{ flex: 1, background: "#ece7dc", borderRadius: 999, height: 7, overflow: "hidden" }}>
                <div style={{ width: `${v}%`, height: "100%", background: color, borderRadius: 999 }} />
              </div>
              <div style={{ width: 30, textAlign: "right", fontSize: "0.72rem", color: "#6b6459" }}>{v}</div>
            </div>
          );
        })}
      </div>

      <div style={{ background: "#fbf3e6", border: "1px solid #efdcc0", borderRadius: 10, padding: "0.8rem 1rem", marginBottom: "0.6rem" }}>
        <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "#a86b1f", marginBottom: 3 }}>⚠ {C.risk}</div>
        <div style={{ fontSize: "0.82rem", color: "#4a453d", lineHeight: 1.5 }}>{a.risk}</div>
      </div>
      <div style={{ background: "#eef5f1", border: "1px solid #cfe3d8", borderRadius: 10, padding: "0.8rem 1rem" }}>
        <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "#2e7d55", marginBottom: 3 }}>✦ {C.need}</div>
        <div style={{ fontSize: "0.82rem", color: "#4a453d", lineHeight: 1.5 }}>{a.need}</div>
      </div>
    </div>
  );
}

export default function NotaireTransmissionCartes({ clientId }) {
  const C = pickLang();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: profiles }, { data: bens }] = await Promise.all([
        supabase
          .from("heir_transmission_profiles")
          .select("beneficiary_id, primary_archetype, confidence, ranking, axes, responses_count")
          .eq("client_id", clientId),
        supabase
          .from("beneficiaries")
          .select("id, name, relation")
          .eq("client_id", clientId)
          .neq("relation", "Institution"),
      ]);
      const nameById = new Map((bens || []).map((b) => [b.id, b.name]));
      const built = (profiles || [])
        .filter((p) => p.primary_archetype)
        .map((p) => ({ ...p, name: nameById.get(p.beneficiary_id) || "Héritier" }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setRows(built);
    } catch (e) {
      console.error("[notaire] cartes transmission:", e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { if (clientId) load(); }, [clientId, load]);

  if (loading) return null;
  if (rows.length === 0) {
    return (
      <section className="mt-8 rounded-2xl border border-dashed border-gray-300 bg-white p-6" style={{ fontFamily: "Georgia, serif" }}>
        <h2 className="text-lg font-bold text-gray-900 mb-1">{C.title}</h2>
        <p className="text-sm text-gray-500 max-w-2xl">{C.none}</p>
      </section>
    );
  }

  return (
    <section className="mt-10">
      <h2 className="text-xl font-bold text-gray-950" style={{ fontFamily: "Georgia, serif" }}>{C.title}</h2>
      <p className="text-sm text-gray-500 mt-1 mb-5 max-w-2xl" style={{ fontFamily: "Georgia, serif" }}>{C.subtitle}</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: "1.25rem", alignItems: "start" }}>
        {rows.map((p) => (
          <Carte key={p.beneficiary_id} profile={p} name={p.name} C={C} />
        ))}
      </div>
    </section>
  );
}
